import { Loader2 } from 'lucide-react'
import { Fragment, ReactNode, useState } from 'react'
import { RxCross2 } from 'react-icons/rx'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Checkbox } from '~/@/components/ui/checkbox-v2'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/@/components/ui/dialog'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

type ConfirmationVariant = 'default' | 'destructive'

interface ConfirmationDialogProps {
  variant?: ConfirmationVariant
  title: string
  description?: ReactNode
  children?: React.ReactNode | React.ReactNode[]
  confirmLabel: string
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  className?: string
  checkboxText?: string
  fullScreen?: boolean
}

/**
 * A reusable confirmation dialog with two variants: default and destructive.
 *
 * Features:
 * - Customizable header with variant-specific styling (default: gray, destructive: red)
 * - Optional description and custom content areas
 * - Optional checkbox confirmation requirement
 * - Loading states with disabled buttons
 * - Configurable button labels, title
 * - Optional custom className
 * - Optional children (can be single node or array of nodes)
 * - Optionally fullScreen mode which moves the checkbox to the footer
 */
export function ConfirmationDialog({
  variant = 'default',
  title,
  description,
  children,
  confirmLabel,
  onConfirm,
  onClose,
  isOpen,
  isLoading = false,
  className,
  checkboxText,
  fullScreen = false,
}: ConfirmationDialogProps) {
  const isDestructive = variant === 'destructive'
  const { t } = useTypedTranslation()
  const [checkboxChecked, setCheckboxChecked] = useState(false)
  const disableConfirmButton = (checkboxText && !checkboxChecked) || isLoading

  const handleClose = () => {
    onClose()
    setCheckboxChecked(false)
  }

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent
        className={cn(
          'border-none p-0 gap-0 flex flex-col justify-between z-[1302]', // Z-index to ensure it appears above other content (Navigation bar has z-index 1201, MUI modals have z-index 1300)
          fullScreen &&
            'fixed inset-0 h-screen w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0',
          className,
        )}
        hideCloseButton
      >
        {/* Header */}
        <DialogHeader
          className={cn(
            'rounded-t-lg',
            isDestructive ? 'bg-red-9 border-red-9 text-white' : 'bg-gray-3 border-gray-3',
            fullScreen && 'rounded-none',
          )}
        >
          <DialogTitle
            className={cn(
              'flex items-center justify-between px-6 py-3 rounded-t-lg',
              isDestructive ? 'bg-red-9 border-red-9 text-white' : 'bg-gray-3 border-gray-5',
            )}
          >
            <h3 className={cn('font-semibold', isDestructive && 'text-white')}>{title}</h3>
            <DialogClose asChild>
              <Button
                className={cn(isDestructive && 'text-white hover:bg-red-10 hover:text-white')}
                disabled={isLoading}
                onClick={handleClose}
                size="smallIcon"
                variant="ghost"
              >
                <RxCross2 className="size-5" />
              </Button>
            </DialogClose>
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div
          className={cn('flex flex-col gap-4 p-6 min-h-20', fullScreen && 'flex-1 overflow-auto')}
        >
          {description && <div className="text-sm leading-relaxed">{description}</div>}

          {/* Checkbox in content for regular modal */}
          {checkboxText && !fullScreen && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={checkboxChecked}
                onCheckedChange={(checked) => setCheckboxChecked(checked === true)}
                variant="secondary"
              />
              <label className="text-sm">{checkboxText}</label>
            </div>
          )}

          {Array.isArray(children) ? (
            <>
              {children.map((child, index) => (
                <Fragment key={index}>{child}</Fragment>
              ))}
            </>
          ) : (
            children
          )}
        </div>

        {/* Footer */}
        <DialogFooter
          className={cn(
            'border-gray-4 w-full items-center border-t px-6 py-3',
            fullScreen ? 'flex-row justify-between sm:justify-between' : '',
          )}
        >
          {/* Checkbox in footer for full-screen */}
          {fullScreen && checkboxText && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={checkboxChecked}
                onCheckedChange={(checked) => setCheckboxChecked(checked === true)}
                variant="secondary"
              />
              <label className="text-sm">{checkboxText}</label>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button disabled={isLoading} onClick={handleClose} size="sm" variant="outline">
              <div className="text-sm">{t('common.actions.cancel')}</div>
            </Button>
            <Button
              className="relative"
              disabled={disableConfirmButton}
              onClick={onConfirm}
              size="sm"
              variant={isDestructive ? 'destructive' : 'secondary'}
            >
              <div className={cn('text-sm', isLoading && 'invisible')}>{confirmLabel}</div>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="size-4 animate-spin" />
                </div>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
